import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useStudioUser } from '../../hooks/useStudioUser';
import { useStudioPlan } from '../../hooks/useStudioPlan';
import Notification from '../Notification';
import UpgradePrompt from '../UpgradePrompt';
import ConnectStripeRequired from './ConnectStripeRequired';
import DOMPurify from 'dompurify';
import {
  PlusIcon,
  TrashIcon,
  PercentBadgeIcon,
} from '@heroicons/react/24/outline';

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  student_position: 2, // Max 2 digits for position
  discount_value: 3, // Max 3 digits for percentage
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string | number, fieldType?: string): string | number => {
  if (typeof input === 'number') {
    // For numbers, just validate range
    if (fieldType === 'student_position') {
      return Math.max(2, Math.min(10, input)); // Clamp between 2-10
    }
    if (fieldType === 'discount_value') {
      return Math.max(0, Math.min(100, input)); // Clamp between 0-100
    }
    return input;
  }
  
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string | number, type: string): boolean => {
  if (value === null || value === undefined) return false;
  
  if (typeof value === 'number') {
    switch (type) {
      case 'student_position':
        return value >= 2 && value <= 10 && Number.isInteger(value);
      case 'discount_value':
        return value >= 0 && value <= 100;
      default:
        return true;
    }
  }
  
  if (typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type) as string;
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'student_position':
      const positionNum = parseInt(sanitized);
      return !isNaN(positionNum) && positionNum >= 2 && positionNum <= 10;
    case 'discount_value':
      const discountNum = parseFloat(sanitized);
      return !isNaN(discountNum) && discountNum >= 0 && discountNum <= 100;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

interface FamilyDiscountRule {
  id: string;
  studio_id: string;
  student_position: number;
  discount_type: 'percentage';
  discount_value: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Family {
  family_id: string;
  student_count: number;
  students: Array<{
    id: string;
    name: string;
    status: string;
  }>;
}

interface FamilyDiscountSettingsProps {
  onClose?: () => void;
}

const FamilyDiscountSettings: React.FC<FamilyDiscountSettingsProps> = ({ onClose }) => {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability, loading: planLoading, isFreePlan, studio } = useStudioPlan();
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [rules, setRules] = useState<FamilyDiscountRule[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState<FamilyDiscountRule | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    student_position: number;
    discount_type: 'percentage';
    discount_value: number | '';
  }>({
    student_position: 2,
    discount_type: 'percentage',
    discount_value: 10,
  });

  const [notification, setNotification] = useState({
    show: false,
    message: '',
    title: '',
    type: 'success' as 'success' | 'error' | 'general',
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    ruleId: string | null;
    ruleName: string;
  }>({ show: false, ruleId: null, ruleName: '' });

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  const fetchRules = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRules(data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch rules');
      }
    } catch (error) {
      logSecurityEvent('RULES_FETCH_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to load family discount rules',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) return;

      setFamiliesLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/families/${studioUser.studio_id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFamilies(data.families || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch families');
      }
    } catch (error) {
      logSecurityEvent('FAMILIES_FETCH_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to load families',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setFamiliesLoading(false);
    }
  };

  useEffect(() => {
    fetchCsrfToken();
  }, []); // Empty dependency array - only run once

  useEffect(() => {
    if (studioUser?.studio_id && csrfToken) {
      fetchRules();
      fetchFamilies();
    }
  }, [studioUser?.studio_id, csrfToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
        type: "error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'submitRule' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
          type: "error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(formData.student_position, 'student_position')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'student_position', value: formData.student_position });
      setNotification({
        show: true,
        message: "Invalid student position",
        title: "Validation Error",
        type: "error",
      });
      return;
    }

    if (!validateInput(formData.discount_value, 'discount_value')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'discount_value', value: formData.discount_value });
      setNotification({
        show: true,
        message: "Invalid discount value",
        title: "Validation Error",
        type: "error",
      });
      return;
    }
    
    // Prevent double submission
    if (saving) {
      return;
    }
    
    // Validate that the selected position is available (when not editing)
    if (!editingRule) {
      const usedPositions = rules.map(rule => rule.student_position);
      if (usedPositions.includes(formData.student_position)) {
        setNotification({
          show: true,
          message: `A rule already exists for the ${formData.student_position}${getOrdinalSuffix(formData.student_position)} student position. Please select a different position.`,
          title: 'Validation Error',
          type: 'error',
        });
        return;
      }
    }
    
    setSaving(true);
    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken || !studioUser?.studio_id) {
        throw new Error('Authentication required');
      }

      let url: string;
      let method: string;
      let body: any = {
        student_position: sanitizeInput(formData.student_position, 'student_position'),
        discount_type: sanitizeInput(formData.discount_type, 'discount_type'),
        discount_value: sanitizeInput(formData.discount_value, 'discount_value'),
      };

      if (editingRule) {
        // For editing, use PUT with the rule ID
        url = `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${editingRule.id}`;
        method = 'PUT';
      } else {
        // For creating, use POST with studio ID
        url = `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${studioUser.studio_id}`;
        method = 'POST';
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const responseRule = await response.json();
        
        if (editingRule) {
          setRules(prev => {
            return prev.map(rule => 
              rule.id === editingRule.id ? responseRule : rule
            );
          });
        } else {
          setRules(prev => {
            // Check if rule with same ID already exists to prevent duplicates
            const existingRule = prev.find(rule => rule.id === responseRule.id);
            if (existingRule) {
              return prev.map(rule => rule.id === responseRule.id ? responseRule : rule);
            }
            return [...prev, responseRule];
          });
        }

        logSecurityEvent('RULE_SAVED_SUCCESSFULLY', {
          action: editingRule ? 'update' : 'create',
          ruleId: responseRule.id,
          studioId: studioUser?.studio_id
        });

        setNotification({
          show: true,
          message: editingRule 
            ? 'Family discount rule updated successfully! Existing Stripe subscriptions are being updated with the new discount rates.' 
            : 'Family discount rule created successfully! Stripe subscriptions are being updated with the new discount rates.',
          title: 'Success',
          type: 'success',
        });

        resetForm();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save rule');
      }
    } catch (error) {
      logSecurityEvent('RULE_SAVE_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        action: editingRule ? 'update' : 'create',
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to save rule',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (rule: FamilyDiscountRule) => {
    const ruleName = `${rule.student_position}${getOrdinalSuffix(rule.student_position)} Student - ${rule.discount_value}% off`;
    setDeleteConfirmation({
      show: true,
      ruleId: rule.id,
      ruleName,
    });
  };

  const handleDeleteConfirm = async () => {
    const { ruleId } = deleteConfirmation;
    if (!ruleId) return;

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
        type: "error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteRule' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
          type: "error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication required');
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/family-discounts/rules/${ruleId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setRules(prev => prev.filter(rule => rule.id !== ruleId));
        
        logSecurityEvent('RULE_DELETED_SUCCESSFULLY', {
          ruleId,
          studioId: studioUser?.studio_id
        });

        setNotification({
          show: true,
          message: 'Family discount rule deleted successfully! Existing Stripe subscriptions are being updated to remove the discount.',
          title: 'Success',
          type: 'success',
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete rule');
      }
    } catch (error) {
      logSecurityEvent('RULE_DELETE_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        ruleId,
        studioId: studioUser?.studio_id
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : 'Failed to delete rule',
        title: 'Error',
        type: 'error',
      });
    } finally {
      setDeleteConfirmation({ show: false, ruleId: null, ruleName: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ show: false, ruleId: null, ruleName: '' });
  };

  const handleEdit = (rule: FamilyDiscountRule) => {
    setEditingRule(rule);
    setFormData({
      student_position: rule.student_position,
      discount_type: rule.discount_type,
      discount_value: rule.discount_value,
    });
    setShowForm(true);
  };

  const initializeForm = () => {
    const usedPositions = rules.map(rule => rule.student_position);
    const availablePositions = [2, 3, 4, 5, 6, 7, 8, 9, 10].filter(pos => !usedPositions.includes(pos));
    const firstAvailablePosition = availablePositions.length > 0 ? availablePositions[0] : 2;
    
    setFormData({
      student_position: firstAvailablePosition,
      discount_type: 'percentage',
      discount_value: 10,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    // Calculate the first available position using the current rules state
    const usedPositions = rules.map(rule => rule.student_position);
    const availablePositions = [2, 3, 4, 5, 6, 7, 8, 9, 10].filter(pos => !usedPositions.includes(pos));
    const firstAvailablePosition = availablePositions.length > 0 ? availablePositions[0] : 2;
    
    setFormData({
      student_position: firstAvailablePosition,
      discount_type: 'percentage',
      discount_value: 10,
    });
    setShowForm(false);
  };

  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const getAvailablePositions = () => {
    const usedPositions = new Set(rules.map(rule => rule.student_position));
    const positions = [];
    for (let i = 2; i <= 10; i++) {
      if (!usedPositions.has(i) || (editingRule && editingRule.student_position === i)) {
        positions.push(i);
      }
    }
    return positions;
  };

  if (planLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
        </div>
      </div>
    );
  }

  if (isFreePlan || !hasCapability('familyDiscounts')) {
    return (
      <div className="space-y-6">
        <UpgradePrompt capability="familyDiscounts" />
      </div>
    );
  }

  if (!studio?.stripe_user_id) {
    return (
      <ConnectStripeRequired
        title="Connect Stripe to configure family discounts"
        description="Family discounts are applied as Stripe coupons on student subscriptions. Connect your Stripe account under Integrations first, then return here to set up your rules."
      />
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Notification
        show={notification.show}
        message={notification.message}
        title={notification.title}
        type={notification.type}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      />

      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Family Discount Rules
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Configure automatic discounts for families with multiple students. 
          Discounts are applied starting with the 2nd student in a family.
        </p>
        {!showForm && (
          <div className="mt-4">
            <button
              type="button"
              onClick={initializeForm}
              className="w-fit xl:w-auto inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4 pointer-events-none" />
              <span className="pointer-events-none">Add Rule</span>
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            {editingRule ? 'Edit Discount Rule' : 'Add Discount Rule'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Student Position
                </label>
                <select
                  value={formData.student_position}
                  onChange={(e) => {
                    const newPosition = parseInt(e.target.value);
                    setFormData(prev => ({ 
                      ...prev, 
                      student_position: newPosition 
                    }));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                >
                  {getAvailablePositions().map(position => (
                    <option key={position} value={position}>
                      {position}{getOrdinalSuffix(position)} Student
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Discount Percentage
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PercentBadgeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow a truly empty field while editing so backspace does not
                      // force a "0" that then becomes values like "015".
                      if (raw === '') {
                        setFormData((prev) => ({ ...prev, discount_value: '' }));
                        return;
                      }
                      const parsed = Number.parseInt(raw, 10);
                      if (Number.isNaN(parsed)) {
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        discount_value: Math.max(0, Math.min(100, parsed)),
                      }));
                    }}
                    min="0"
                    max="100"
                    step="1"
                    className="block w-full pl-10 rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="10"
                    required
                    maxLength={MAX_LENGTHS.discount_value}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter percentage (0-100)
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <span className="pointer-events-none">Cancel</span>
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="pointer-events-none">{saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Add Rule')}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {rules.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <PercentBadgeIcon className="h-12 w-12" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No discount rules
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating your first family discount rule.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={initializeForm}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5 pointer-events-none" />
                <span className="pointer-events-none">Add First Rule</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {rules.map((rule, index) => (
                <li key={`${rule.id}-${rule.student_position}-${index}`} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <PercentBadgeIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {DOMPurify.sanitize(`${rule.student_position}${getOrdinalSuffix(rule.student_position)} Student`, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {DOMPurify.sanitize(`${rule.discount_value}% off`, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(rule)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 text-sm font-medium"
                      >
                        <span className="pointer-events-none">Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(rule)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 text-sm font-medium"
                      >
                        <span className="pointer-events-none">Delete</span>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {rules.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                How Family Discounts Work
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                <ul className="list-disc list-inside space-y-1">
                  <li>Discounts are automatically applied to students based on their position in the family</li>
                  <li>The 1st student in a family pays full price (no discount)</li>
                  <li>Starting with the 2nd student, discounts are applied according to these rules</li>
                  <li>When a student leaves a family, discounts are automatically recalculated for remaining members</li>
                  <li>Discounts are applied to Stripe subscriptions when family billing is set up</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Family Management Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Family Management
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              View family groups and their associated students.
            </p>
          </div>
        </div>

        {familiesLoading ? (
          <div className="animate-pulse">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        ) : families.length === 0 ? (
          <div className="text-center py-8">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No families found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Families will appear here when students are assigned family IDs.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {families.map((family) => (
                <li key={family.family_id} className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {DOMPurify.sanitize(family.family_id, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {family.student_count} student{family.student_count !== 1 ? 's' : ''}
                        </div>
                        {family.students && family.students.length > 0 && (
                          <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            {family.students.slice(0, 3).map(student => student.name).join(', ')}
                            {family.students.length > 3 && ` +${family.students.length - 3} more`}
                          </div>
                        )}
                      </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 w-full max-w-md">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={handleDeleteCancel}
              >
                <span className="sr-only pointer-events-none">Close</span>
                <svg className="h-6 w-6 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="text-left">
              <h3 className="mb-5 text-lg font-semibold text-gray-900 dark:text-white">
                Are you sure you want to delete this discount rule?
              </h3>
              <div className="mb-5 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {DOMPurify.sanitize(deleteConfirmation.ruleName, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                </p>
              </div>
              <div className="flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-4">
                <button
                  onClick={handleDeleteCancel}
                  className="w-full xl:w-auto py-2.5 px-5 text-sm font-medium text-gray-900 dark:text-gray-400 focus:outline-none bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <span className="pointer-events-none">Cancel</span>
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="w-full xl:w-auto text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center justify-center px-5 py-2.5"
                >
                  <span className="pointer-events-none">Yes, Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FamilyDiscountSettings;